import React, { useEffect, useState } from "react";
import { clone, remove } from "lodash";
import { Content } from "../../components/Layout";
import { AttacmentBox, BorderedField, Box, Flex } from "../../components/Flex";
import { Label, LoadingCircle, Paragraph, Title } from "../../components/Text";
import { Button } from "../../components/Button";
import { CloseIcon } from "../../icons/CloseIcon";
import { JIRA_URL } from "../../settings";
import { Scrollbar } from "../../components/Scrollbar";
import { IssueUserIcon } from "../../icons/IssueUserIcon";
import { fixPackageSize, formatDate } from "../../utils";
import { SortBlockArrowIcon } from "../../icons/SortBlockArrowIcon";
import { PadlockIcon } from "../../icons/PadlockIcon";
import { FormTooltip } from "../../components/Tooltip";
import { IssueComment } from "../../components/Input";
import { RemoveIcon } from "../../icons/RemoveIcon";

const IssueDetails = ({
    title,
    data,
    loading,
    saveComment,
    renderFieldContent,
    onClose,
    pingServer,
    downloadAttachment,
    deleteAttachment,
    updateAttachment
}) => {
    const initialState = {
        sorted: true,
        focus: false,
        comment: null,
        attachments: [],
        visible: true
    };
    const [ state, setState ] = useState(initialState);

    useEffect(() => {
        if (state.focus) {
            pingServer();
        };
    }, [ state.focus ]);

    const fields = {
        description: [
            { label: "OS version", attr: "osversion" },
            { label: "System Architecture", attr: "sysarch" },
            { label: "Product", attr: "product" },
            { label: "Product Version", attr: "version" }
        ],
        details: [
            { label: "Reporter", attr: "submittedBy" },
            { label: "Email", attr: "email" },
            { label: "Organization", attr: "orgName" },
            { label: "Created", attr: "submitDatetime" },
            { label: "Status", attr: "status"}
        ],
        link: [
            { label: "Jira Link", attr: "ticketRef", render: d => <a target="_blank" rel="noreferrer" href={`${JIRA_URL}/browse/${d}`} >{`${JIRA_URL}/browse/${d}`}</a> }
        ]
    };

    const sortComments = (comments, order) => {
        let sorted = comments.slice(0).sort((a, b) => a.created.localeCompare(b.created));
        return order
            ? sorted.reverse()
            : sorted
    };

    const renderComments = (comments) => {
        return Array.isArray(comments)
            ? sortComments(comments, state.sorted).map((comment) =>(
                <Flex key={comment.key} gap={{ right: 15 }}>
                    <IssueUserIcon />
                    <Flex direction="column" padding={{ top: "7px" }}>
                        <Flex align="center" margin={{ bottom: "7px" }}>
                            <Title style={{ marginRight: "8px" }}>{comment.author}</Title>
                            <Paragraph lineHeight="22px" style={{ marginRight: "20px" }}>{formatDate(comment.created, "MMMM D, YYYY, h:mm A")}</Paragraph>
                            <FormTooltip text={`Visible to ${comment.visibility ? "all" : "VSI"} users`}>
                                <PadlockIcon small locked={!comment.visibility} style={{ color: "#CDD8ED" }} />
                            </FormTooltip>
                        </Flex>
                        <Paragraph breakWord whiteSpace="pre-line">{comment.body}</Paragraph>
                    </Flex>
                </Flex>
            ))
            : loading
                ? <Flex gap={{ right: 15 }}>
                    <LoadingCircle />
                    <Paragraph isLoading width="100%" />
                </Flex>
                : null
    };

    const cropFilename = (str) => {
        return str.length > 16
          ? str.substr(0, 10) + '...' + str.substr(str.length-6, str.length)
          : str
    };

    const renderAttachments = (attachments) => {
        return (
            <Flex direction="column" style={{ overflow: "hidden" }}>
                <Paragraph color="var(--sp-label-color)" lineHeight="22px">Attachments{Array.isArray(attachments) ? " (" + attachments.length + ")" : ""}:</Paragraph>
                {Array.isArray(attachments)
                    ? attachments.length
                        ? <Flex wrap="wrap" margin={{ left: "-8px", top: 0, right: "-8px", bottom: 0 }} style={{ textAlign: "center" }}>
                            {attachments.map((attachment) => (
                                <AttacmentBox
                                    key={attachment.id}
                                    removeAttachment={() => deleteAttachment(attachment.id)}
                                    downloadAttachment={() => downloadAttachment(attachment.id, attachment.filename)}
                                >
                                    <Label>{formatDate(attachment.created, "MMM D, YYYY, h:mm A")}</Label>
                                    <Flex justify="center" gap={{ right: 4 }}>
                                        <Paragraph>{cropFilename(attachment.filename)}</Paragraph>
                                        {(attachment.visible == 0 || attachment.visible == 1) &&
                                            <PadlockIcon small locked={attachment.visible == 0} onClick={() => updateAttachment(attachment.id, attachment.visible == 0)} style={{ color: "#CDD8ED" }} />}
                                    </Flex>
                                </AttacmentBox>
                            ))}
                        </Flex>
                        : null
                    : loading
                        ? <Paragraph isLoading width="100%" />
                        : null}
            </Flex>
        );
    };

    return(
        <Content background="#FFFFFF" gap={{ bottom: 18 }}>
            <Flex width="100%" justify="space-between" margin={{ bottom: "24px"}}>
                <Title h4 isLoading={loading} color="var(--sp-label-color)">
                    [{title}]
                </Title>
                <CloseIcon onClick={onClose} />
            </Flex>
            <Flex height="100%">
                <Flex direction="column" flex={1} margin={{ right: "17px"}} width="calc(100% - 405px)">
                    <Scrollbar>
                        <Flex direction="column" padding={{ right: "12px" }}>
                            <Title h4 isLoading={loading} margin={{ bottom: "14px"}}>{data.summary}</Title>
                            <Flex direction="column">
                                <Title h6 capitalize margin={{ bottom: "4px" }}>description</Title>
                                {renderFieldContent(fields.link, data)}
                                <Flex direction="column">
                                    {renderFieldContent(fields.description, data)}
                                </Flex>
                                <Paragraph margin={{ bottom: "16px" }}>{data.description}</Paragraph>
                                {renderAttachments(data.attachments)}
                            </Flex>
                        </Flex>
                    </Scrollbar>
                    <Flex flex={1} justify="space-between" padding={{ bottom: "7px" }} margin={{ top: "16px", bottom: "16px", right: "12px" }} style={{ borderBottom: "2px solid #D7E3F7" }}>
                        <Label bold>comments</Label>
                        <Flex align="center" gap={{ right: 5 }} onClick={() => setState(state => ({ ...state, sorted: !state.sorted }))} cursor="pointer">
                            <Label>
                                <SortBlockArrowIcon descend={state.sorted} />
                            </Label>
                            <Label bold>
                                {state.sorted ? "new" : "old"}est first
                            </Label>
                        </Flex>
                    </Flex>
                    <Scrollbar>
                        <Flex margin={{ bottom: "16px" }} gap={{ right: 15 }} padding={{ right: "12px" }}>
                            <IssueUserIcon commenting />
                            {state.focus
                                ? <Flex direction="column" flex={1} gap={{ bottom: 12 }} width="calc(100% - 47px)">
                                    <IssueComment
                                        text={state.comment}
                                        pingServer={pingServer}
                                        onTextChange={value => setState(state => ({ ...state, comment: value }))}
                                        onFileChange={file => {
                                            let files = clone(state.attachments);
                                            files.push(Object.assign(file, { key: files.length }));
                                            setState({ ...state, attachments: files });
                                        }}
                                    />
                                    {state.attachments.length
                                        ? state.attachments.map((attachment, idx) => (
                                                <Box key={`attachment-${idx}`} flatten background="#F0F4FB" direction="row" align="center" justify="space-between">
                                                    <Title nowrap max="calc(100% - 104px)">{attachment.name}</Title>
                                                    <Flex align="center" gap={{ right: 20 }}>
                                                        <Paragraph nowrap lineHeight="24px">{fixPackageSize(attachment.size)}</Paragraph>
                                                        <RemoveIcon
                                                            color="var(--sp-danger-color)"
                                                            hoverColor="var(--sp-danger-hover-color)"
                                                            onClick={() => {
                                                                let files = clone(state.attachments);
                                                                remove(files, { key: attachment.key })
                                                                setState(state => ({ ...state, attachments: files }));
                                                            }}
                                                        />
                                                    </Flex>
                                                </Box>))
                                        : null
                                    }
                                    <Flex align="center" justify="space-between">
                                        <Flex gap={{ right: 16 }}>
                                            <Button disabled={state.comment === null && !state.attachments.length} onClick={() => { saveComment({ comment: state.comment, files: state.attachments, visible: state.visible }); setState(initialState)}}>Save</Button>
                                            <Button type="link" onClick={() => setState(state => ({ ...state, focus: false }))}>Cancel</Button>
                                        </Flex>
                                        <Flex align="center" gap={{ right: 8 }}>
                                            <Label bold lineHeight="20px">visible to {state.visible ? "all" : "VSI"} users</Label>
                                            <Label>
                                                <PadlockIcon
                                                    locked={!state.visible}
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setState(state => ({ ...state, visible: !state.visible }))}
                                                />
                                            </Label>
                                        </Flex>
                                    </Flex>
                                </Flex>
                                : <BorderedField
                                    flex={1}
                                    padding={{ top: "10px", left: "12px", right: "12px", bottom: "10px" }}
                                    onClick={() => setState(state => ({ ...state, focus: true }))}
                                >
                                    <Paragraph>Add a comment or an attachment...</Paragraph>
                                </BorderedField>}
                        </Flex>
                        <Flex direction="column" gap={{ bottom: 16 }} padding={{ right: "12px" }}>
                            {renderComments(data.comments)}
                        </Flex>
                    </Scrollbar>
                </Flex>
                <Box flatten background="#F0F4FB" gap={{ bottom: 4 }} width="390px">
                    <Title h6>Created by</Title>
                    {renderFieldContent(fields.details, data)}
                </Box>
            </Flex>
        </Content>
    );
};

export { IssueDetails }